// Copyright © 2021 Move Closer
var ImageModuleUi_1;
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import VueEasyLightbox from 'vue-easy-lightbox';
import { BootstrapLink } from '../../dsl/front';
import { AbstractModuleUi } from '../_abstract/ui';
import { FileType } from '../_contracts';
import { ImageWithCaption } from '../_components/ui';
import { prepareImage } from '../_filters';
import { FileDataActions } from './Image.contracts';
/**
 * Component capable of rendering the `ImageModule`'s contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
let ImageModuleUi = ImageModuleUi_1 = class ImageModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        this.action = FileDataActions;
        /**
         * The image to render.
         */
        this.image = null;
        /**
         * Determines state of full screen preview
         */
        this.isImageFullScreen = false;
        this.link = '';
        this.hideAuthor = false;
        this.hideDescription = false;
        this.sponsored = false;
    }
    /**
     * @inheritDoc
     * @override
     */
    get articlesCount() {
        return 0;
    }
    get _imageRatio() {
        if (typeof this.module.content.imageRatio === 'undefined' || this.module.content.imageRatio.length === 0) {
            return this.imageRatio;
        }
        return this.module.content.imageRatio;
    }
    /**
     * Checks whether image can be displayed full screen
     */
    get canHaveFullScreenView() {
        return this.module.content.action === this.action.Preview;
    }
    /**
     * Method to check if image has link
     */
    get hasLink() {
        return !!this.link;
    }
    /**
     * Closes full screen view of image
     */
    closeFullScreenPreview() {
        this.isImageFullScreen = false;
    }
    /**
     * Opens image in the full screen mode
     */
    openFullScreenPreview() {
        if (!this.canHaveFullScreenView) {
            return;
        }
        this.isImageFullScreen = true;
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        let imageFile = null;
        let content = null;
        const { alt, author, caption } = this.module.content;
        const inlineOverrides = {
            alt,
            author,
            caption
        };
        this.hideAuthor = this.module.content.hideAuthor || false;
        this.hideDescription = this.module.content.hideDescription || false;
        if (this.module.content.useAsAd) {
            this.hideAuthor = true;
            this.hideDescription = false;
            this.sponsored = true;
            inlineOverrides.caption = this.$t('_.ad');
        }
        if (typeof this.module.content.image === 'object' &&
            this.module.content.image !== null &&
            Number.isInteger(this.module.content.image.value)) {
            imageFile = this.relatedService.resolveSync(this.module.content.image, {});
            if (!imageFile) {
                this.onError();
                return;
            }
            imageFile = ImageModuleUi_1.applyInlineOverrides(imageFile, inlineOverrides);
            let maxWidth = this.isMobile ? 768 : 1220;
            if (this.module.content.maxWidth) {
                maxWidth = this.module.content.maxWidth;
            }
            // If image is used as ad, set max width to 1920 Temporary solution
            if (this.module.content.useAsAd) {
                maxWidth = 1920;
            }
            this.image = prepareImage(imageFile, this._imageRatio, maxWidth, this.module.isLazyLoaded);
            this.onReady();
        }
        else if (typeof this.module.content.src === 'string') {
            try {
                const src = this.module.content.src;
                imageFile = {
                    id: '',
                    mime: '',
                    orgMime: '',
                    size: '',
                    srcset: [{
                            height: null,
                            url: src,
                            width: 1
                        }],
                    type: FileType.Image,
                    url: src
                };
                imageFile = ImageModuleUi_1.applyInlineOverrides(imageFile, inlineOverrides);
                const maxWidth = this.isMobile ? 768 : 1220;
                this.image = prepareImage(imageFile, this._imageRatio, maxWidth, this.module.isLazyLoaded);
                this.onReady();
            }
            catch (e) {
                this.onError(e);
            }
        }
        else {
            this.onError();
        }
        if (this.module.content.redirectExt && typeof this.module.content.redirectExt === 'string') {
            this.link = this.module.content.redirectExt;
        }
        else if (this.module.content.redirect) {
            if (typeof this.module.content.redirect === 'object' &&
                Number.isInteger(this.module.content.redirect.value)) {
                content = await this.relatedService.resolveSync(this.module.content.redirect, {});
                if (!content) {
                    this.onError();
                    return;
                }
                this.link = content.urlPath;
                this.onReady();
            }
        }
    }
    /**
     * Merges the passed-in source `ImageFile` with the additional inline overrides.
     *
     * @param imageFile - Source `ImageFile` object to merge INTO.
     * @param inlineOverrides - Values to inject into the `ImageFile` object.
     */
    static applyInlineOverrides(imageFile, inlineOverrides) {
        inlineOverrides = this.flushUndefinedOverrides(inlineOverrides);
        return { ...imageFile, ...inlineOverrides };
    }
    /**
     * Flushes (removes) the properties with the undefined values.
     *
     * @param inlineOverrides - Values to inject into the `ImageFile` object.
     */
    static flushUndefinedOverrides(inlineOverrides) {
        return Object.entries(inlineOverrides).reduce((acc, [key, value]) => (typeof value === 'undefined' ? acc : {
            ...acc,
            [key]: value
        }), {});
    }
};
ImageModuleUi = ImageModuleUi_1 = __decorate([
    Component({
        name: 'ImageModuleUi',
        components: {
            BootstrapLink,
            ImageWithCaption,
            VueEasyLightbox
        },
        async created() {
            await this.fetchRelated();
        },
        template: `
    <div>
    <template v-if="hasLink">
      <BootstrapLink :link="{ target: link, newTab: true, sponsored }" class="text-decoration-none">
        <ImageWithCaption v-if="image || debug" v-bind="{ image }" :isLazy="module.isLazyLoaded"
                          :hideDescription="hideDescription"
                          :hideAuthor="hideAuthor"
                          class="module" :class="[debugClass, loadingClass]"/>
      </BootstrapLink>
    </template>

    <template v-else>
      <ImageWithCaption v-if="image || debug" v-bind="{ image }" :isLazy="module.isLazyLoaded"
                        :canPreview="canHaveFullScreenView"
                        :hideDescription="hideDescription"
                        :hideAuthor="hideAuthor"
                        class="module preview-image" :class="[debugClass, loadingClass]"
                        @click="openFullScreenPreview"/>
      <VueEasyLightbox
        v-if="image && canHaveFullScreenView"
        :visible="isImageFullScreen"
        :imgs="image.fullscreen"
        @hide="closeFullScreenPreview"/>
    </template>
    </div>`
    })
], ImageModuleUi);
export { ImageModuleUi };
